import React, { useMemo, useState, useEffect } from "react";

export default function LevelsGrid({ levels = [], currentLevel }) {
  const PAGE_SIZE = 20;

  const totalPages = Math.max(1, Math.ceil(levels.length / PAGE_SIZE));

  const getPageForCurrent = () => {
    // Prefer real position in the array (works even if levels aren't 1..n)
    const idx = levels.findIndex(
      (x) => Number(x.level) === Number(currentLevel),
    );
    if (idx >= 0)
      return Math.min(Math.max(Math.floor(idx / PAGE_SIZE) + 1, 1), totalPages);

    // Fallback: assume level number maps to order
    const lvl = Number(currentLevel);
    if (!Number.isFinite(lvl) || lvl < 1) return 1;
    return Math.min(Math.max(Math.ceil(lvl / PAGE_SIZE), 1), totalPages);
  };

  const [page, setPage] = useState(1);

  // Open the page that contains currentLevel (also handles async levels loading)
  useEffect(() => {
    setPage(getPageForCurrent());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLevel, levels.length]);

  // If page becomes invalid after levels change, clamp it
  useEffect(() => {
    setPage((p) => Math.min(Math.max(p, 1), totalPages));
  }, [totalPages]);

  const pagedLevels = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return levels.slice(start, start + PAGE_SIZE);
  }, [levels, page]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="levelsWrap">
      <style>{`
        .levelsWrap { width: 100%; }
        .pager {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }
        .pagerInfo { color: #B5BBC0; font-size: 13px; font-weight: 700; text-align: center; }
        .paginate-btn {
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #2A4051;
          font-weight: 700;
        }
        .btnEnabled { background: #223241; color: #E6ECF1; cursor: pointer; }
        .btnDisabled { background: #1a2836; color: #8798A7; cursor: not-allowed; }
        .grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 18px 14px;
        }

        @media (max-width: 640px) {
          .pager { flex-direction: column; align-items: stretch; }
          .pagerInfo { order: 0; }
          .pagerBtns { display: flex; gap: 10px; }
          .pagerBtns .paginate-btn { flex: 1; }
          .grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
        }

        @media (max-width: 380px) {
          .grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Pagination Controls */}
      <div className="pager">
        <div className="pagerBtns" style={{ display: "flex", gap: 12 }}>
          <button
            type="button"
            onClick={() => canPrev && setPage((p) => p - 1)}
            disabled={!canPrev}
            className={`paginate-btn ${canPrev ? "btnEnabled" : "btnDisabled"}`}
          >
            <img
              src="/icons/arrow-left.svg"
              alt="Next"
              style={{ width: 15, height: 15, fontWeight: 500 }}
            />
            Prev
          </button>

          <button
            type="button"
            onClick={() => canNext && setPage((p) => p + 1)}
            disabled={!canNext}
            className={`paginate-btn ${canNext ? "btnEnabled" : "btnDisabled"}`}
          >
            Next
            <img
              src="/icons/arrow-left.svg"
              alt="Next"
              style={{ width: 15, height: 15, transform: "rotate(180deg)" }}
            />
          </button>
        </div>

        <div className="pagerInfo">
          Page {page} / {totalPages}{" "}
          <span style={{ fontWeight: 400 }}>
            (Levels {(page - 1) * PAGE_SIZE + 1}-
            {Math.min(page * PAGE_SIZE, levels.length)})
          </span>
        </div>
      </div>

      {/* Grid */}
      <div className="grid">
        {pagedLevels.map((item) => {
          const isCurrent = Number(item.level) === Number(currentLevel);
          const locked = Number(item.level) > Number(currentLevel);

          return (
            <div
              key={item.level}
              style={{
                height: 64,
                display: "flex",
                alignItems: "center",
                gap: 12,
                minWidth: 0,
                borderRadius: 10,
                backgroundColor: locked ? "#1a2836" : "#223241",
                border: isCurrent
                  ? "2px solid var(--Primary-500)"
                  : "1px solid #2A4051",
                boxShadow: isCurrent
                  ? "0 0 0 3px color-mix(in srgb, var(--Primary-500), transparent 75%)"
                  : "none",
                justifyContent: "center",
                padding: "5px",
              }}
            >
              {/* ICON */}
              {!locked ? (
                <div
                  style={{
                    height: 34,
                    width: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 12,
                    backgroundColor: "var(--Primary-500)",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {item.level}
                </div>
              ) : (
                <div
                  style={{
                    height: 34,
                    width: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#D0D0D0",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src="/icons/lock.svg"
                    alt="Locked"
                    style={{ width: 18, height: 18 }}
                  />
                </div>
              )}

              {/* TEXT */}
              <div style={{ lineHeight: 1.2, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: locked ? "#8798A7" : "#E6ECF1",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Level {item.level}
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: "#B5BBC0",
                    marginTop: 4,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.user_percentage}% of members
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
